function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeMatchText(value = "") {
  return safeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function getRefId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return safeText(value._id || value.id);
}

function titleCase(value = "") {
  return safeText(value)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      if (/^#?\d+$/.test(word)) return word;
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
    })
    .join(" ");
}

function formatCombinedLabel(value = "") {
  return safeText(value).replace(/\s*\/\s*/g, " or ").replace(/\s+/g, " ");
}

function pluralize(base = "item", count = 0) {
  const safeBase = safeText(base) || "item";
  return Number(count) === 1 ? safeBase : `${safeBase}s`;
}

function cleanCustomerUnit(value = "") {
  return safeText(value)
    .replace(/^per\s+/i, "")
    .replace(/^covered\s+/i, "")
    .replace(/\bbutton\s+hole\b/gi, "buttonhole")
    .trim();
}

function getServiceMatchText(value = {}) {
  return normalizeMatchText(
    `${value?.name || ""} ${value?.slug || ""} ${value?.label || ""} ${
      value?.optionLabel || ""
    } ${value?.unitLabel || ""}`,
  );
}

export const SERVICE_PRICING_CONTEXTS = Object.freeze({
  buttonHole: {
    key: "buttonHole",
    title: "Button Hole Service",
    paragraphs: [
      "Choose the type of garment or finish you need. Garment prices include up to 10 buttonholes or eyelets.",
      "Need more than 10? Any extras are charged individually.",
    ],
    defaultIncludedQuantity: 10,
    defaultIncludedItemLabel: "buttonholes/eyelets",
  },
  coverButton: {
    key: "coverButton",
    title: "Cover Button Service",
    paragraphs: [
      "Choose the style and size you need. Covered buttons are priced individually.",
    ],
    defaultPricingUnit: "each",
  },
});

export function getServiceKind(value = {}) {
  const text = getServiceMatchText(value);

  if (text.includes("coverbutton") || text.includes("coveredbutton")) {
    return "coverButton";
  }

  if (
    text.includes("buttonhole") ||
    text.includes("buttonholes") ||
    text.includes("eyelet") ||
    text.includes("eyelets")
  ) {
    return "buttonHole";
  }

  return "";
}

export function getServiceInfoForCatalogRef(ref = null) {
  const kind = getServiceKind(ref || {});
  return kind ? SERVICE_PRICING_CONTEXTS[kind] : null;
}

function getProductRef(product = {}, key = "") {
  if (key === "subCategory") {
    return product.subCategory || product.subcategory || null;
  }

  return product.category || null;
}

function getMatchingProductRef(products = [], key = "", id = "") {
  const safeId = safeText(id);
  if (!safeId) return null;

  for (const product of products) {
    const ref = getProductRef(product, key);
    if (getRefId(ref) === safeId) return ref;
  }

  return null;
}

function getSharedServiceRef(products = [], key = "") {
  const refs = products.map((product) => getProductRef(product, key)).filter(Boolean);
  if (!refs.length) return null;

  const firstId = getRefId(refs[0]);
  if (!firstId || refs.some((ref) => getRefId(ref) !== firstId)) return null;

  return getServiceInfoForCatalogRef(refs[0]) ? refs[0] : null;
}

export function getServiceCatalogContext({
  products = [],
  categoryId = "",
  subCategoryId = "",
} = {}) {
  const selectedSubCategory = getMatchingProductRef(
    products,
    "subCategory",
    subCategoryId,
  );
  const selectedCategory = getMatchingProductRef(products, "category", categoryId);

  const catalogRef =
    selectedSubCategory ||
    selectedCategory ||
    getSharedServiceRef(products, "subCategory") ||
    getSharedServiceRef(products, "category");

  const serviceInfo = getServiceInfoForCatalogRef(catalogRef);

  return {
    catalogRef,
    serviceInfo,
    title: serviceInfo?.title || safeText(catalogRef?.name),
  };
}

export function getCustomerProductName(product = {}, serviceContext = null) {
  const explicitName =
    safeText(product.customerDisplayName) ||
    safeText(product.displayName) ||
    safeText(product.name);

  if (!explicitName) return "Service";

  const kind = serviceContext?.key || getServiceKind(product);
  const text = explicitName.replace(/\s+/g, " ").trim();

  if (kind === "buttonHole") {
    const buttonHoleMatch = text.match(/^button\s*holes?\s+(.+)$/i);
    if (buttonHoleMatch) {
      return `Button Holes — ${titleCase(buttonHoleMatch[1])}`;
    }

    const eyeletsMatch = text.match(/^eyelets?\s+(.+)$/i);
    if (eyeletsMatch) {
      const detail = titleCase(eyeletsMatch[1]);
      if (/^threaded$/i.test(detail)) return "Threaded Eyelet";
      return detail ? `Eyelets — ${detail}` : "Eyelets";
    }

    if (/^keyhole with bar end$/i.test(text)) return "Keyhole with Bar End";
    if (/^straight keyhole with bar end$/i.test(text)) {
      return "Straight Keyhole with Bar End";
    }

    return titleCase(text);
  }

  if (kind === "coverButton") {
    const shortMatch = text.match(/^(domed|ring|flat\s*top)\s*#?(\d+)$/i);
    if (shortMatch) {
      return `${titleCase(shortMatch[1])} Cover Button — #${shortMatch[2]}`;
    }

    const commaMatch = text.match(/^(.+?),\s*cover\s*button\s*#?(\d+)$/i);
    if (commaMatch) {
      return `${titleCase(commaMatch[1])} Cover Button — #${commaMatch[2]}`;
    }

    const match = text.match(/^cover\s*button\s+(.+?)\s*#?(\d+)$/i);
    if (match) {
      return `${titleCase(match[1])} Cover Button — #${match[2]}`;
    }

    return titleCase(text);
  }

  return text;
}

export function getIncludedLabel(quantity = 0, itemLabel = "") {
  const safeQuantity = Math.floor(Number(quantity));
  if (!Number.isFinite(safeQuantity) || safeQuantity <= 0) return "";

  const label = safeText(itemLabel);
  if (!label) return `Up to ${safeQuantity} included`;

  return `Up to ${safeQuantity} ${formatCombinedLabel(label)} included`;
}

export function getServiceOptionIncludedLabel(option = {}, service = {}) {
  const quantity = Math.floor(
    Number(option.includedQuantity ?? service.includedQuantity ?? 0),
  );
  const unit = cleanCustomerUnit(option.unitLabel || service.unitLabel);
  const kind = getServiceKind({ ...service, ...option });

  if (quantity > 0) {
    return getIncludedLabel(
      quantity,
      option.includedItemLabel || service.includedItemLabel,
    );
  }

  if (kind !== "buttonHole" || unit !== "garment") return "";

  const text = getServiceMatchText(option);
  if (text.includes("eyelet") && !text.includes("buttonhole")) {
    return getIncludedLabel(10, "eyelets");
  }
  if (text.includes("period")) {
    return getIncludedLabel(10, "buttonholes / eyelets");
  }

  return getIncludedLabel(10, "buttonholes");
}

export function getServiceDisplayName(service = {}) {
  const kind = getServiceKind(service);
  if (kind) return SERVICE_PRICING_CONTEXTS[kind].title;
  return safeText(service.name) || "Service";
}

export function getServiceOptionDisplayName(option = {}, service = {}) {
  const label = safeText(option.label || option.optionLabel || option.name);
  const kind = getServiceKind({ ...service, ...option, label });

  if (kind === "coverButton") {
    return getCustomerProductName({ name: label }, SERVICE_PRICING_CONTEXTS.coverButton);
  }

  if (kind === "buttonHole") {
    if (/^button\s*hole$/i.test(label)) return "Buttonhole";
    if (/^button\s*holes?$/i.test(label)) return "Buttonholes";
    if (/^eyelets?$/i.test(label)) return "Eyelets";
    return getCustomerProductName({ name: label }, SERVICE_PRICING_CONTEXTS.buttonHole);
  }

  return label || "Service";
}

export function getOptionQuestionLabel(service = {}, option = {}) {
  const kind = getServiceKind(service);
  const optionText = getServiceMatchText(option);

  if (kind === "coverButton") return "Choose a covered button style";
  if (kind === "buttonHole" && optionText.includes("eyelet")) {
    return "Choose an eyelet style";
  }
  if (kind === "buttonHole") return "Choose a buttonhole style";

  return "Choose the service you need";
}

export function getQuantityQuestion(service = {}, option = {}) {
  const kind = getServiceKind(service);
  const optionText = getServiceMatchText(option);

  if (kind === "coverButton") return "How many buttons?";
  if (cleanCustomerUnit(option.unitLabel || service.unitLabel) === "garment") {
    return "How many garments?";
  }
  if (kind === "buttonHole" && optionText.includes("eyelet")) {
    return "How many eyelets?";
  }
  if (kind === "buttonHole") return "How many buttonholes?";

  const unit = cleanCustomerUnit(option.unitLabel || service.unitLabel || "items");
  return `How many ${pluralize(unit, 2)}?`;
}

export function getIncludedFinishQuestion(service = {}, option = {}) {
  const optionText = getServiceMatchText(option);
  const kind = getServiceKind(service);
  const isGarmentService =
    cleanCustomerUnit(option.unitLabel || service.unitLabel) === "garment";

  if (kind !== "buttonHole") return "";
  if (optionText.includes("eyelet") && !optionText.includes("buttonhole")) {
    if (isGarmentService) return "How many eyelets does each garment need?";
    return "How many eyelets do you need?";
  }
  if (optionText.includes("period")) {
    if (isGarmentService) {
      return "How many buttonholes or eyelets does each garment need?";
    }
    return "How many buttonholes or eyelets do you need?";
  }

  if (isGarmentService) return "How many buttonholes does each garment need?";

  return "How many buttonholes do you need?";
}

export function getQuantitySummary(quantity = 0, service = {}, option = {}) {
  const count = Math.max(0, Math.floor(Number(quantity || 0)));
  const kind = getServiceKind(service);
  const optionText = getServiceMatchText(option);
  const unit = cleanCustomerUnit(option.unitLabel || service.unitLabel || "item");

  if (kind === "coverButton") return `${count} ${pluralize("button", count)}`;
  if (unit === "garment") return `${count} ${pluralize("garment", count)}`;
  if (kind === "buttonHole" && optionText.includes("eyelet")) {
    return `${count} ${pluralize("eyelet", count)}`;
  }
  if (kind === "buttonHole") {
    return `${count} ${pluralize("buttonhole", count)}`;
  }

  return `${count} ${pluralize(unit, count)}`;
}

export function getPriceLabel(value = {}, currency = "GBP") {
  const price = Number(value.price ?? value.unitPrice ?? value.basePrice ?? 0);
  const unit =
    getServiceKind(value) === "coverButton"
      ? "each"
      : cleanCustomerUnit(value.pricingUnit || value.unitLabel);

  const formatted = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: safeText(currency) || "GBP",
  }).format(Number.isFinite(price) ? price : 0);

  if (!unit) return formatted;
  if (unit === "each" || unit === "button") return `${formatted} each`;

  return `${formatted} per ${unit.replace(/^per\s+/i, "")}`;
}

export function formatServiceMethod(value = "") {
  const normalized = safeText(value).toLowerCase();

  if (normalized === "drop_off") return "Drop off at our shop";
  if (normalized === "ship_to_store") return "Send to our shop";
  if (normalized === "collect") return "Collect from our shop";
  if (normalized === "return_shipping") return "Post back to me";

  return titleCase(normalized || "Not set");
}

export function getServiceStatusLabel(value = "") {
  const normalized = safeText(value).toLowerCase();

  if (normalized === "pending_payment") return "Awaiting payment";
  if (normalized === "payment_processing") return "Payment processing";
  if (normalized === "payment_failed") return "Payment failed";
  if (normalized === "ready_for_collection") return "Ready for collection";
  if (normalized === "ready_to_ship") return "Ready to post back";
  if (normalized === "in_progress") return "In progress";
  if (normalized === "awaiting_customer") return "Waiting for your reply";

  return titleCase(normalized || "Pending");
}
